import { useCallback, useEffect } from 'react'

import { useConfig } from '@/hooks/useConfig'
import { isInIframe, sendGTMEvent } from '@/utils/gtm'

export function useTagManager() {
  const { config } = useConfig()

  useEffect(() => {
    if (!config?.tagManagerEnabled || !import.meta.env.PROD) return

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'GTM_EVENT' && event.data?.data) {
        console.log('[GTM] Received postMessage event:', event.data.data)
        sendGTMEvent(event.data.data.event, event.data.data)
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [config?.tagManagerEnabled])

  const sendEvent = useCallback(
    (event: string, eventData?: Record<string, any>) => {
      if (!config?.tagManagerEnabled) return console.log('[GTM] Tag manager disabled, event not sent:', event)

      if (!import.meta.env.PROD) return console.log('[GTM] Development environment, event not sent:', event)

      const dataLayerEvent = { event, ...eventData }
      console.log('[GTM] Processing event:', dataLayerEvent)

      if (!isInIframe()) {
        console.log('[GTM] Normal context, sending to local dataLayer')
        return sendGTMEvent(event, eventData)
      }

      console.log('[GTM] Detected iframe context')
      const targetWindow: any = window.top || window.parent

      let sentToParent = false

      try {
        if (targetWindow?.dataLayer) {
          console.log('[GTM] Sending directly to parent dataLayer')
          targetWindow.dataLayer.push(dataLayerEvent)
          sentToParent = true
        }
      } catch {
        console.log('[GTM] Cannot access parent dataLayer (cross-origin)')
      }

      if (!sentToParent && targetWindow && targetWindow !== window) {
        console.log('[GTM] Sending postMessage to parent')
        targetWindow.postMessage({ type: 'GTM_EVENT', data: dataLayerEvent }, '*')
        sentToParent = true
      }

      console.log('[GTM] Sending to local dataLayer (iframe backup)')
      sendGTMEvent(event, eventData)
    },
    [config?.tagManagerEnabled]
  )

  return { sendGTMEvent: sendEvent }
}
